import { getDpr } from '@readcloud/common';
import { ErrorType, PageLabel, PageLabelList } from '@readcloud/data';

import { decodeJwt } from '@readcloud/common';
import { Book, PDFData, UserState } from '@readcloud/data';
import { EpubCFI } from 'epubjs';
import Locations from 'epubjs/types/locations';

import * as pdfjs from 'pdfjs-dist';
import {
  ClientSmallBrand,
  clientSmallBrandToSchemeMap,
} from '../types/TopToolbarConstants';

import pdfWorker from 'pdfjs-dist/build/pdf.worker.js?url';

import {
  PDFDataRangeTransport,
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
} from 'pdfjs-dist';
import { AuthState } from '@readcloud/state';
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

import { round, getAsr, MAX_SQUARE_VIEWPORT_LENGTH } from '../constants';

const ctxForDpr = document
  .createElement('canvas')
  .getContext('2d', { alpha: false });
if (!ctxForDpr) {
  throw new Error('Could not create canvas context');
}

/**
 * Multiply your intended viewport scale with this when calculating the render viewport
 * (the viewport that is given to the render task, and the viewport that is used to size your canvas).
 *
 * It is DPR * DPI Upscaler (96/72 because we want to go from 72 DPI, which is what the PDF will be rendered in by default, to 96 DPI)
 */
export const PAGE_SCALE_FACTOR = (getDpr(ctxForDpr) * 96) / 72;

/**
 * Given a displayViewport that is sized correctly, return a render viewport that should be used for the pdf page render task, and for the canvas.height and canvas.width...NOT for the canvas' CSS dimensions
 * This viewport is upscaled depending on your DPR, and by 96/72 by default for extra quality, as pages are 72 dpi by default.
 *
 * Optionally, you can provide a pageScaleFactor, multiply the render quality by a constant (can cause higher or lower resolution)
 *
 * You can also provide a constantWidth to ensure that the viewport's width conforms to your requirements (for example, you might have
 * dimension requirements for generated images of PDF pages). In this case, pageScaleFactor is irrelevant and can't be provided
 *
 *
 */
export const makeRenderViewport = ({
  displayViewport,
  ...other
}: {
  displayViewport: pdfjs.PageViewport;
} & ({ constantWidth: number } | { pageScaleFactor: number } | {})) => {
  return displayViewport.clone({
    scale: round(
      'pageScaleFactor' in other
        ? displayViewport.scale * other.pageScaleFactor
        : 'constantWidth' in other
        ? displayViewport.scale * (other.constantWidth / displayViewport.width)
        : displayViewport.scale //* PAGE_SCALE_FACTOR // - I'm not sure whether this is important, but with it the page is scaled wrong
    ),
  });
};

/**
 * Use this to create a display viewport, which should be used for display purposes
 * (set css height and width to the dimensions of viewports that this returns)
 *
 */
export const makeDisplayViewport = (props: {
  unitViewport: pdfjs.PageViewport;
  maxHeight: number;
  maxWidth: number;
}) => {
  const { unitViewport } = props;

  const containmentAsr = getAsr({
    height: props.maxHeight,
    width: props.maxWidth,
  });
  const pageAsr = getAsr({
    height: unitViewport.height,
    width: unitViewport.width,
  });
  const relativeAsr = containmentAsr / pageAsr;
  return relativeAsr > 1
    ? unitViewport.clone({
        scale: round(props.maxHeight / unitViewport?.height),
      })
    : unitViewport.clone({
        scale: round(props.maxWidth / unitViewport?.width),
      });
};

/**
 * Use this to get yourself a PDFJSDocument, given our data format, startOffset you can do all the things
 */
export const getPDFDocument = (props: {
  data: PDFData;
  onProgress?: (progress: { loaded: number; total: number }) => void;
}): {
  loadingTask?: PDFDocumentLoadingTask;
  pdfDocument: Promise<PDFDocumentProxy>;
  error?: ErrorType;
} => {
  let pdfDocRequest: PDFDocumentLoadingTask | undefined = undefined;
  try {
    // Add debugging to check the data being passed
    console.log('getPDFDocument called with data type:', typeof props.data);
    console.log(
      'getPDFDocument data instanceof ArrayBuffer:',
      props.data instanceof ArrayBuffer
    );
    console.log(
      'getPDFDocument data instanceof Uint8Array:',
      props.data instanceof Uint8Array
    );
    console.log(
      'getPDFDocument data byteLength:',
      props.data instanceof ArrayBuffer ? props.data.byteLength : 'N/A'
    );
    console.log(
      'getPDFDocument data length (if Uint8Array):',
      props.data instanceof Uint8Array ? props.data.length : 'N/A'
    );

    // Check if data is valid
    if (!props.data) {
      console.error('getPDFDocument: data is null or undefined');
      throw new Error('PDF data is null or undefined');
    }

    if (props.data instanceof ArrayBuffer && props.data.byteLength === 0) {
      console.error('getPDFDocument: ArrayBuffer is empty');
      throw new Error('PDF data is empty');
    }

    if (props.data instanceof Uint8Array && props.data.length === 0) {
      console.error('getPDFDocument: Uint8Array is empty');
      throw new Error('PDF data is empty');
    }

    pdfDocRequest = pdfjs.getDocument({
      ...(typeof props.data === 'string'
        ? {
            url: props.data as string | URL,
          }
        : props.data instanceof PDFDataRangeTransport
        ? {
            range: props.data as PDFDataRangeTransport,
            rangeChunkSize: 65536 * 4,
          }
        : {
            data: props.data as ArrayBuffer,
          }),
    });

    if (props.onProgress) pdfDocRequest.onProgress = props.onProgress;

    const pdfDocument = pdfDocRequest.promise;

    return {
      loadingTask: pdfDocRequest,
      pdfDocument: pdfDocument,
      error: undefined,
    };
  } catch (error) {
    console.error('getPDFDocument error:', error);
    if (pdfDocRequest) {
      pdfDocRequest.destroy();
    }
    return {
      loadingTask: undefined,
      pdfDocument: Promise.reject(error),
      error: { type: 'DocumentError', error: error as Error },
    };
  }
};

export const limitViewport = (viewport: pdfjs.PageViewport) => {
  const unitViewport = viewport.clone({ scale: 1 });
  const pageAsr = getAsr(unitViewport);
  const maxScale = round(
    MAX_SQUARE_VIEWPORT_LENGTH / pageAsr / unitViewport.height
  );

  if (viewport.scale > maxScale) {
    return viewport.clone({
      scale: maxScale,
    });
  } else {
    return viewport;
  }
};

/**
 * Produces an array that gives you pageIndex -> pageLabel: {value: string, label: string}
 */
export async function makePageLabels(pdfDocument: pdfjs.PDFDocumentProxy) {
  return (
    pdfDocument &&
    pdfDocument.getPageLabels().then((labels) =>
      labels
        ? labels.map((label, i) => ({ value: `${i + 1}`, label }))
        : Array.from(Array(pdfDocument.numPages), (_, i) => ({
            value: `${i + 1}`,
            label: `${i + 1}`,
          }))
    )
  );
}

export const getPageLabelByString = (
  location: string,
  locations?: Locations,
  pageLabelsByLocation?: { [k: string]: PageLabel }
) => {
  const epubCfi = new EpubCFI();
  return `${
    pageLabelsByLocation && pageLabelsByLocation[location]
      ? pageLabelsByLocation[location].label
      : epubCfi && epubCfi.isCfiString(location) && locations
      ? locations.locationFromCfi(location).toString()
      : location
  }`;
};

//learning toolbar utils
